import {
  customDateToLocalDayjs,
  dayjsToCustomDate,
  getPeriodReferentialInterval,
  isPeriodGranularity,
  isPeriodPresetOption,
  parsePeriodValue,
  resolvePeriodRange,
  serializeDateRange,
  type PeriodGranularityType,
  type PeriodReferentialIntervalType
} from '#domain/core/filter/period';
import { TreeSelectionZodSchema, type TreeSelectionType } from '#domain/core/filterDefinition/types/treeSelection.type';
import { type FilterDefinition } from '@carrefour-gcs/bff-sdk';
import {
  createFilterNodeId,
  DEFAULT_CARREFOUR_REFERENTIAL_INTERVAL,
  FILTER_NODE_ID_LEVEL_SEPARATOR,
  splitFilterNodeId,
  type AllFilterType,
  type CarrefourReferentialIntervalType,
  type ComparisonPeriodType,
  type FiltersStateType,
  type NullableProps,
  type ProductReferentialFilterType,
  type ReferentialIntervalsType,
  type SimpleFilter,
  type StoreReferentialFilterType
} from '@carrefour-gcs/shared';
import { DB_TO_LEGACY_MAP } from './filterMapping.config';
import {
  COMPARISON_MODES,
  CUSTOM_HISTORY_FIELD,
  DATE_SELECTION_OPTIONS,
  FILTER_TARGET,
  GRANULARITY_FIELD,
  HISTORY_FIELD,
  HISTORY_OPTIONS,
  PERIOD_FIELD,
  PERIOD_TO_GRANULARITY_MAP
} from './constants/filterMapping.constant';
import type { BaseFilterMappingType, DatePeriodIntervalType, LegacyCustomDateType } from './types/filterMapping.type';

/**
 * This function transforms an array of strings (filter selected) into a string array interpreted by all dashboards
 * @example Input : ["CAC", "HYP"]
 *          Output : ["chainType=CAC", "chainType=HYP"]
 */
export function adaptSelectedFilter(
  key: keyof FiltersStateType,
  filterKey: ProductReferentialFilterType | SimpleFilter | StoreReferentialFilterType | AllFilterType,
  value?: unknown
) {
  const valueIsAnArrayOfStrings = value && Array.isArray(value) && value.every((item) => typeof item === 'string');

  if (valueIsAnArrayOfStrings) {
    const valueAlreadyHasFilterKey = value.every((item) => item.split('=')[0] === filterKey);
    if (valueAlreadyHasFilterKey) {
      return value;
    }

    return value.map((item) => `${filterKey}=${item}`);
  } else {
    console.error(
      `[FiltersStore - adaptSelectedFilter] Invalid value for ${key}: expected an array of strings, got`,
      value
    );
  }
}

/**
 * Tree selections → legacy nodeIds, the only place producing the grammar for tree filters.
 * @example [{ path: [{ type: "sector", key: "010" }, { type: "department", key: "012" }] }] → ["sector=010##department=012"]
 */
export function treeSelectionsToLegacyNodeIds(key: keyof FiltersStateType, value?: unknown) {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (!Array.isArray(value)) {
    console.error(
      `[FiltersStore - treeSelectionsToLegacyNodeIds] Invalid value for ${key}: expected an array, got`,
      value
    );
    return [];
  }

  return value.flatMap((item) => {
    const selection = TreeSelectionZodSchema.safeParse(item);

    if (!selection.success || !selection.data.path.every((segment) => isLegacySafeKey(segment.key))) {
      console.error(`[FiltersStore - treeSelectionsToLegacyNodeIds] Invalid selection for ${key}, skipping`, item);
      return [];
    }

    const nodeId = selection.data.path.reduce<string | undefined>(
      (parentNodeId, segment) => createFilterNodeId({ filterType: segment.type, filterKey: segment.key, parentNodeId }),
      undefined
    );

    return nodeId ? [nodeId] : [];
  });
}

/**
 * A key holding a grammar separator would be truncated when the nodeId is split back apart.
 */
function isLegacySafeKey(key: string): boolean {
  return !key.includes('=') && !key.includes(FILTER_NODE_ID_LEVEL_SEPARATOR);
}

/**
 * Reverse of {@link treeSelectionsToLegacyNodeIds}.
 * @example ["sector=010##department=012"] → [{ path: [{ type: "sector", key: "010" }, { type: "department", key: "012" }] }]
 */
export function legacyNodeIdsToTreeSelections(legacyValue?: string[]): TreeSelectionType[] {
  const valueIsAnArrayOfStrings =
    legacyValue && Array.isArray(legacyValue) && legacyValue.every((item) => typeof item === 'string');

  if (!valueIsAnArrayOfStrings) {
    console.error(
      '[FiltersStore - legacyNodeIdsToTreeSelections] Invalid value: expected an array of nodeId strings, got',
      legacyValue
    );
    return [];
  }

  return legacyValue.flatMap((nodeId) => {
    const selection = TreeSelectionZodSchema.safeParse({
      path: splitFilterNodeId(nodeId).map(([type, key]) => ({ type, key }))
    });

    if (!selection.success) {
      console.error('[FiltersStore - legacyNodeIdsToTreeSelections] Invalid nodeId, skipping', nodeId);
      return [];
    }

    return [selection.data];
  });
}

type PeriodMappingContextType = {
  granularity: PeriodGranularityType | null;
  referentialInterval: PeriodReferentialIntervalType | null;
};

/**
 * Turns the `period` field value into the legacy `selectedPeriod`. Undefined when it cannot be resolved
 * (Nielsen or missing granularity, missing refresh state, unsupported value): the legacy period is then
 * left untouched rather than replaced by a broken one.
 */
export function toLegacyPeriod(
  raw: unknown,
  { granularity, referentialInterval }: PeriodMappingContextType
): DatePeriodIntervalType | undefined {
  const value = parsePeriodValue(raw);
  const range = granularity ? resolvePeriodRange(raw, granularity, referentialInterval) : null;

  if (!value || !range || !granularity) {
    return undefined;
  }

  return {
    option: value.kind === 'preset' ? value.option : DATE_SELECTION_OPTIONS.DATE_RANGE,
    period: granularity,
    periodInterval: { start: dayjsToCustomDate(range.start), end: dayjsToCustomDate(range.end) }
  };
}

/** Reverse of {@link toLegacyPeriod}: the preset key, or the explicit range of a custom period. */
export function serializeLegacyPeriod(legacyValue: unknown): string | undefined {
  const legacy = legacyValue as Partial<DatePeriodIntervalType> | undefined;
  const start = legacy?.periodInterval?.start;
  const end = legacy?.periodInterval?.end;

  // Nielsen intervals are period codes, not calendar dates: they have no date range equivalent
  if (!isLegacyCustomDate(start) || !isLegacyCustomDate(end)) {
    return undefined;
  }

  const option = legacy?.option;

  if (isPeriodPresetOption(option)) {
    return option;
  }

  return serializeDateRange({ start: customDateToLocalDayjs(start), end: customDateToLocalDayjs(end) });
}

/**
 * Builds the legacy `comparisonPeriod` from the two DB filters that drive it.
 *
 * The `history` radio picks the mode and `custom_history` carries the explicit range used when
 * the mode is custom. Anything that cannot be resolved to a real interval falls back to the
 * default N-1 comparison, so the KPI queries always receive a usable comparison period.
 */
export function buildComparisonPeriod(history: unknown, customHistory: unknown): ComparisonPeriodType {
  const previousYear: ComparisonPeriodType = { comparisonMode: COMPARISON_MODES.PREVIOUS_YEAR };

  if (history !== HISTORY_OPTIONS.CUSTOM || typeof customHistory !== 'string') {
    return previousYear;
  }

  const range = resolvePeriodRange(customHistory);

  if (!range) {
    return previousYear;
  }

  return {
    comparisonMode: COMPARISON_MODES.CUSTOM,
    periodInterval: { start: dayjsToCustomDate(range.start), end: dayjsToCustomDate(range.end) }
  };
}

/**
 * Reverse of {@link buildComparisonPeriod}: turns the legacy `comparisonPeriod` back into the
 * explicit date range expected by the `custom_history` picker.
 * Returns null when the comparison is not a custom one.
 */
export function serializeComparisonPeriodToDateRange(comparisonPeriod: unknown): string | null {
  const period = comparisonPeriod as ComparisonPeriodType | undefined;

  if (period?.comparisonMode !== COMPARISON_MODES.CUSTOM || !period.periodInterval) {
    return null;
  }

  const { start, end } = period.periodInterval;

  // Nielsen intervals are period codes, not calendar dates: they have no date range equivalent.
  if (!isLegacyCustomDate(start) || !isLegacyCustomDate(end)) {
    return null;
  }

  return serializeDateRange({ start: customDateToLocalDayjs(start), end: customDateToLocalDayjs(end) });
}

/**
 * Orchestrates current state retrieval, falling back to legacy configuration trees when needed.
 */
export function resolveInitialFieldValue(
  def: FilterDefinition,
  currentFieldsValue: Record<string, Record<string, unknown>>,
  rootFilters: Record<string, unknown> | undefined,
  legacySelectedFilters: Record<string, unknown>
): unknown {
  const groupName = def.group?.name ?? '';
  const localValue = currentFieldsValue[groupName]?.[def.name];

  if (localValue !== undefined) {
    return localValue;
  }

  return resolveLegacyFieldValue(def.name, rootFilters, legacySelectedFilters);
}

/**
 * Loops over incoming data to build clean root and nested key updates. The `period` needs the submitted
 * granularity and the refresh state of the page, which is why they travel along the data.
 */
export function partitionFilterUpdates(
  data: Record<string, unknown>,
  referentialIntervals?: ReferentialIntervalsType,
  referentialIntervalKey: keyof CarrefourReferentialIntervalType = DEFAULT_CARREFOUR_REFERENTIAL_INTERVAL
): {
  rootUpdates: Record<string, unknown>;
  nestedUpdates: Partial<NullableProps<FiltersStateType>>;
} {
  const rootUpdates: Record<string, unknown> = {};
  const nestedUpdates: Partial<NullableProps<FiltersStateType>> = {};

  const periodContext = buildPeriodMappingContext(
    data[GRANULARITY_FIELD],
    referentialIntervals,
    referentialIntervalKey
  );

  for (const [dbKey, rawValue] of Object.entries(data)) {
    const mapping = DB_TO_LEGACY_MAP[dbKey as keyof typeof DB_TO_LEGACY_MAP];

    if (!mapping) {
      continue;
    }

    if (dbKey === PERIOD_FIELD) {
      const legacyPeriod = toLegacyPeriod(rawValue, periodContext);

      if (legacyPeriod) {
        rootUpdates[mapping.legacyKey] = legacyPeriod;
      }
      continue;
    }

    const transformedValue = transformFilterValue(rawValue, mapping);

    if (mapping.target === FILTER_TARGET.ROOT) {
      rootUpdates[mapping.legacyKey] = transformedValue;
    } else {
      nestedUpdates[mapping.legacyKey] = transformedValue as NullableProps<FiltersStateType>[typeof mapping.legacyKey];
    }
  }

  // `history` and `custom_history` are two DB fields collapsing into the single legacy
  // `comparisonPeriod`, which the 1-to-1 DB_TO_LEGACY_MAP cannot express on its own.
  if (HISTORY_FIELD in data || CUSTOM_HISTORY_FIELD in data) {
    rootUpdates['comparisonPeriod'] = buildComparisonPeriod(data[HISTORY_FIELD], data[CUSTOM_HISTORY_FIELD]);
  }

  return { rootUpdates, nestedUpdates };
}

function buildPeriodMappingContext(
  rawGranularity: unknown,
  referentialIntervals: ReferentialIntervalsType | undefined,
  referentialIntervalKey: keyof CarrefourReferentialIntervalType
): PeriodMappingContextType {
  const granularity = isPeriodGranularity(rawGranularity) ? rawGranularity : null;

  return {
    granularity,
    referentialInterval: granularity
      ? getPeriodReferentialInterval(referentialIntervals, referentialIntervalKey, granularity)
      : null
  };
}

/** Transforms a raw database value to its legacy shape. */
function transformFilterValue(rawValue: unknown, mapping: BaseFilterMappingType): unknown {
  const toLegacyFn = mapping.toLegacy as ((val: unknown) => unknown) | undefined;

  return toLegacyFn ? toLegacyFn(rawValue) : rawValue;
}

// --- Internal Extraction Helpers ---

/**
 * Narrows a legacy interval bound to a calendar date, excluding Nielsen period codes.
 */
function isLegacyCustomDate(bound: unknown): bound is LegacyCustomDateType {
  const candidate = bound as Partial<LegacyCustomDateType> | undefined;

  return (
    typeof candidate?.year === 'number' && typeof candidate.month === 'number' && typeof candidate.day === 'number'
  );
}

/**
 * Resolves legacy database mapping conversions for fields.
 */
function resolveLegacyFieldValue(
  fieldName: string,
  rootFilters: Record<string, unknown> | undefined,
  legacySelectedFilters: Record<string, unknown>
): unknown {
  // Both derive from the single legacy `comparisonPeriod`, so they are resolved before the map lookup.
  if (fieldName === HISTORY_FIELD) {
    return resolveHistoryValue(rootFilters);
  }

  if (fieldName === CUSTOM_HISTORY_FIELD) {
    return serializeComparisonPeriodToDateRange(rootFilters?.['comparisonPeriod']) ?? undefined;
  }

  const mapping = DB_TO_LEGACY_MAP[fieldName as keyof typeof DB_TO_LEGACY_MAP];

  if (!mapping) {
    return undefined;
  }

  const legacyValue = getLegacyRawValue(mapping, rootFilters, legacySelectedFilters);

  if (fieldName === 'granularity') {
    return resolveGranularityValue(rootFilters, legacyValue);
  }

  if (mapping.toDB && legacyValue != null) {
    const transform = mapping.toDB as (value: unknown) => unknown;
    return transform(legacyValue);
  }

  return legacyValue;
}

/**
 * Retrieves the raw value from either the root or nested legacy store based on target mapping.
 */
function getLegacyRawValue(
  mapping: BaseFilterMappingType,
  rootFilters: Record<string, unknown> | undefined,
  legacySelectedFilters: Record<string, unknown>
): unknown {
  if (mapping.target === FILTER_TARGET.ROOT) {
    return rootFilters?.[mapping.legacyKey];
  }

  return legacySelectedFilters[mapping.legacyKey];
}

/**
 * Maps the legacy comparison mode back onto the `history` radio option.
 */
function resolveHistoryValue(rootFilters: Record<string, unknown> | undefined): string {
  const comparisonPeriod = rootFilters?.['comparisonPeriod'] as ComparisonPeriodType | undefined;

  return comparisonPeriod?.comparisonMode === COMPARISON_MODES.CUSTOM
    ? HISTORY_OPTIONS.CUSTOM
    : HISTORY_OPTIONS.PREVIOUS_YEAR;
}

/**
 * Custom extraction strategy for the granularity field based on legacy selected period.
 */
function resolveGranularityValue(rootFilters: Record<string, unknown> | undefined, legacyValue: unknown): unknown {
  const legacyPeriodObj = rootFilters?.['selectedPeriod'] as DatePeriodIntervalType | undefined;

  if (legacyPeriodObj?.period) {
    return PERIOD_TO_GRANULARITY_MAP[legacyPeriodObj.period] ?? legacyValue;
  }

  return legacyValue;
}
